import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listGodowns, createGodown, updateGodown, deleteGodown } from '@/app/services/inventoryService';
import { listSites } from '@/app/services/siteService';
import { Godown, GodownCreate, GodownUpdate } from '@/app/types/inventory';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Pencil, Plus, MoreHorizontal, Trash2, AlertTriangle } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import GodownForm from './GodownForm';

const GodownManagement: React.FC = () => {
  const queryClient = useQueryClient();
  
  // State for managing dialogs
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  // Selected godown for edit/delete
  const [selectedGodown, setSelectedGodown] = useState<Godown | null>(null);

  // Get godowns
  const {
    data: godownsData,
    isLoading: isLoadingGodowns,
    isError: isErrorGodowns,
    error: godownsError,
  } = useQuery({
    queryKey: ['godowns'],
    queryFn: () => listGodowns({}),
  });

  // Get sites for form dropdowns
  const {
    data: sitesData,
    isLoading: isLoadingSites,
    isError: isErrorSites,
  } = useQuery({
    queryKey: ['sites'],
    queryFn: () => listSites({}),
  });

  // Create godown mutation
  const createGodownMutation = useMutation({
    mutationFn: (data: GodownCreate) => createGodown(data),
    onSuccess: () => {
      toast.success('Godown created successfully');
      queryClient.invalidateQueries({ queryKey: ['godowns'] });
      setIsCreateDialogOpen(false);
    },
    onError: (error: Error) => {
      const errorDetail = (error as any).response?.data?.detail || 'An unknown error occurred';
      toast.error('Failed to create godown', {
        description: errorDetail,
      });
    }
  });

  // Update godown mutation
  const updateGodownMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: GodownUpdate }) => updateGodown(id, data),
    onSuccess: () => {
      toast.success('Godown updated successfully');
      queryClient.invalidateQueries({ queryKey: ['godowns'] });
      setIsEditDialogOpen(false);
      setSelectedGodown(null);
    },
    onError: (error: Error) => {
      const errorDetail = (error as any).response?.data?.detail || 'An unknown error occurred';
      toast.error('Failed to update godown', {
        description: errorDetail,
      });
    }
  });

  // Delete godown mutation
  const deleteGodownMutation = useMutation({
    mutationFn: (id: number) => deleteGodown(id),
    onSuccess: () => {
      toast.success('Godown deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['godowns'] });
      setIsDeleteDialogOpen(false);
      setSelectedGodown(null);
    },
    onError: (error: Error) => {
      const errorDetail = (error as any).response?.data?.detail || 'An unknown error occurred';
      toast.error('Failed to delete godown', {
        description: errorDetail,
      });
    }
  });

  // Handle create godown submit
  const handleCreateSubmit = (data: GodownCreate | GodownUpdate): void => {
    // We know this is GodownCreate in create mode
    createGodownMutation.mutate(data as GodownCreate);
  };

  // Handle edit godown submit
  const handleEditSubmit = (data: GodownCreate | GodownUpdate): void => {
    if (selectedGodown) {
      // We know this is GodownUpdate in edit mode
      updateGodownMutation.mutate({ 
        id: selectedGodown.id, 
        data: data as GodownUpdate 
      });
    }
  };

  // Handle edit godown button click
  const handleEditClick = (godown: Godown): void => {
    setSelectedGodown(godown);
    setIsEditDialogOpen(true);
  };

  // Handle delete godown button click
  const handleDeleteClick = (godown: Godown): void => {
    setSelectedGodown(godown);
    setIsDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = (): void => {
    if (selectedGodown) {
      deleteGodownMutation.mutate(selectedGodown.id);
    }
  };

  // Show loading state
  if (isLoadingGodowns || isLoadingSites) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading godowns...</span>
      </div>
    );
  }

  // Show error state
  if (isErrorGodowns) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Failed to load godowns: {(godownsError as Error)?.message || 'Unknown error'}
        </AlertDescription>
      </Alert>
    );
  }

  // Show error for sites
  if (isErrorSites) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Failed to load sites data needed for forms.
        </AlertDescription>
      </Alert>
    );
  }

  // Sites are filtered by organization in the backend, so we just use the items directly
  const sites = sitesData?.items || [];

  return (
    <div className="space-y-4">
      {/* Header with create button */}
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-muted-foreground">
            Manage your storage locations (godowns)
          </p>
        </div>
        <Button 
          onClick={() => setIsCreateDialogOpen(true)}
          className="flex items-center"
        >
          <Plus className="mr-2 h-4 w-4" /> Add New Godown
        </Button>
      </div>

      {/* Godowns table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Site</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {godownsData && godownsData.items.length > 0 ? (
              godownsData.items.map((godown) => (
                <TableRow key={godown.id}>
                  <TableCell className="font-medium">{godown.name}</TableCell>
                  <TableCell>{godown.code || '-'}</TableCell>
                  <TableCell>
                    {[
                      godown.address,
                      godown.city,
                      godown.state,
                      godown.pincode,
                    ]
                      .filter(Boolean)
                      .join(', ') || '-'}
                  </TableCell>
                  <TableCell>{godown.site?.name || '-'}</TableCell>
                  <TableCell>
                    <Badge variant={godown.is_active ? "default" : "outline"}>
                      {godown.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <span className="sr-only">Open menu</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={() => handleEditClick(godown)}
                          className="cursor-pointer"
                        >
                          <Pencil className="mr-2 h-4 w-4" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDeleteClick(godown)}
                          className="cursor-pointer text-destructive focus:text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                  No godowns found. Start by adding a new godown.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create Godown Form Dialog */}
      <GodownForm
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSubmit={handleCreateSubmit}
        isLoading={createGodownMutation.isPending}
        mode="create"
        sites={sites}
      />

      {/* Edit Godown Form Dialog */}
      {selectedGodown && (
        <GodownForm
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          onSubmit={handleEditSubmit}
          defaultValues={selectedGodown}
          isLoading={updateGodownMutation.isPending}
          mode="edit"
          sites={sites}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the godown &quot;{selectedGodown?.name}&quot;.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteGodownMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GodownManagement; 